export const state = () => ({
	conversations: null,
	conversation: null,
	messages: null,
	has_unread_messages: false,
	page: null,
	scroll_position: 0,
	archived: false,
});

export const mutations = {
	setConversations(state: any, payload: any) {
		state.conversations = payload;
	},

	setConversation(state: any, payload: any) {
		state.conversation = payload;
	},

	setMessages(state: any, payload: any) {
		state.messages = payload;
	},

	setHasUnreadMessages(state: any, payload: boolean) {
		state.has_unread_messages = payload;
	},

	setPage(state: any, payload: any) {
		state.page = payload;
	},

	setScrollPosition(state: any, payload: any) {
		state.scroll_position = payload;
	},

	setArchived(state: any, payload: boolean) {
		state.archived = payload;
	},
};

export const getters = {
	getConversations: (state: any) => state.conversations,

	getConversation: (state: any) => state.conversation,

	getMessages: (state: any) => state.messages,

	getHasUnreadMessages: (state: any) => state.has_unread_messages,

	getPage: (state: any) => state.page,

	getScrollPosition: (state: any) => state.scroll_position,

	getArchived: (state: any) => state.archived,
};

export const actions = {
	getConversations(state: any, payload: any) {
		let params = {
			page: state.state.page,
			archived: state.state.archived ? '1' : '0',
		};

		Object.keys(params).forEach((key) => {
			if (!params[key]) delete params[key];
		});

		return new Promise((resolve, reject) => {
			this.$axios
				.get(`/api/conversations`, { params })
				.then((res) => {
					let conversations;

					if (state.state.conversations) {
						conversations = JSON.parse(
							JSON.stringify(state.state.conversations),
						);
					}

					if (params.page) {
						conversations.data.push(...res.data.data);
						conversations.links = res.data.links;
						conversations.meta = res.data.meta;
					} else {
						conversations = res.data;
					}

					state.commit('setConversations', conversations);
					state.commit(
						'setHasUnreadMessages',
						conversations.meta.has_unread_messages || false,
					);
					resolve(res.data);
				})
				.catch((err) => {
					reject(err);
				});
		});
	},

	getConversation(state: any, conversation_uuid: string) {
		return new Promise((resolve, reject) => {
			this.$axios
				.get(`/api/conversations/${conversation_uuid}`, {
					params: { read: 1 },
				})
				.then((res) => {
					const conversation = res.data;

					state.dispatch('markConversationAsRead', conversation_uuid);
					state.commit('setConversation', conversation);
					state.commit('setMessages', conversation.messages);
					resolve(conversation);
				})
				.catch((err) => {
					reject(err);
				});
		});
	},

	appendConversation(state: any, payload: any) {
		const { conversation_uuid, ...params } = payload;

		return new Promise((resolve, reject) => {
			this.$axios
				.get(`/api/conversations/${conversation_uuid}`, { params })
				.then((res) => {
					let conversations =
						JSON.parse(JSON.stringify(state.state.conversations)) ??
						[];

					conversations.data = conversations.data.filter(
						(item) => item.uuid !== conversation_uuid,
					);

					conversations.data.push({
						...res.data,
						has_unread_messages: true,
					});

					state.commit('setConversations', conversations);
					resolve(res.data);
				})
				.catch((err) => {
					reject(err);
				});
		});
	},

	appendMessage(state: any, message_uuid: string) {
		return new Promise((resolve, reject) => {
			this.$axios
				.get(`/api/messages/${message_uuid}`)
				.then((res) => {
					const newMessage = res.data;

					if (
						state.state.conversation.uuid !==
						newMessage.conversation.uuid
					) {
						return resolve(newMessage);
					}

					let messages = JSON.parse(
						JSON.stringify(state.state.messages),
					);

					messages.push(newMessage);

					state.dispatch(
						'updateConversationTimestamp',
						newMessage.conversation.uuid,
					);
					state.commit('setMessages', messages);
					resolve(newMessage);
				})
				.catch((err) => {
					reject(err);
				});
		});
	},

	sendMessage(state: any, payload: any) {
		const { conversation_uuid, data } = payload;

		return new Promise((resolve, reject) => {
			this.$axios
				.post(`/api/conversations/${conversation_uuid}/messages`, data)
				.then((res) => {
					const newMessage = res.data;

					let messages = JSON.parse(
						JSON.stringify(state.state.messages),
					);

					messages.push(newMessage);

					state.dispatch(
						'updateConversationTimestamp',
						conversation_uuid,
					);
					state.commit('setMessages', messages);
					resolve(newMessage);
				})
				.catch((err) => {
					reject(err);
				});
		});
	},

	archiveConversation(state: any, conversation_uuid: string) {
		return new Promise((resolve, reject) => {
			this.$axios
				.put(`/api/conversations/${conversation_uuid}/archive-status`)
				.then((res) => {
					let conversations = JSON.parse(JSON.stringify(state.state.conversations));
					let conversation = JSON.parse(JSON.stringify(state.state.conversation));

					if (conversations) {
						// Remove the conversation from the state if the archive filter
						// that is set is not the same as the updated status and add it
						// to the state if the filter and the updated status is the same.
						state.state.archived === Boolean(res.data.archived_at)
							? conversations.data.push(res.data)
							: conversations.data = conversations.data.filter((i) => i.uuid !== res.data.uuid);

						// Update the archived conversations count.
						res.data.archived_at
							? conversations.meta.archived_conversation_count++
							: conversations.meta.archived_conversation_count--;

						// Set the archived status to unarchived and get the conversations if
						// the state's archived_conversation_count is 0.
						if (!conversations.meta.archived_conversation_count) {
							state.commit('setArchived', false);
							state.dispatch('getConversations');
						}
					}

					// Update the opened conversation's archived status.
					if (conversation.uuid === res.data.uuid) {
						conversation.archived_at = res.data.archived_at;
					}

					state.commit('setConversations', conversations);
					state.commit('setConversation', conversation);
					resolve(res.data);
				})
				.catch((err) => {
					reject(err);
				});
		});
	},

	setHasUnreadMessages(state: any, payload: boolean) {
		state.commit('setHasUnreadMessages', payload);
	},

	setPage(state: any, payload: any) {
		state.commit('setPage', payload);
	},

	setScrollPosition(state: any, payload: any) {
		state.commit('setScrollPosition', payload);
	},

	markConversationAsRead(state: any, conversation_uuid: string) {
		let conversations = JSON.parse(
			JSON.stringify(state.state.conversations),
		);

		conversations.data = conversations.data.map((conversation) => {
			if (conversation.uuid === conversation_uuid) {
				conversation.has_unread_messages = false;
			}
			return conversation;
		});

		if (!conversations.data.filter((c) => c.has_unread_messages).length) {
			state.commit('setHasUnreadMessages', false);
		}

		state.commit('setConversations', conversations);
	},

	markCandidateMessagesAsRead(state: any, conversation_uuid: string) {
		if (state.state.conversation?.uuid !== conversation_uuid) return;

		let messages = JSON.parse(JSON.stringify(state.state.messages));

		messages = messages.map((message) => {
			if (message.sender_type === 'Candidate' && !message.read_at) {
				message.read_at = new Date();
			}
			return message;
		});

		state.commit('setMessages', messages);
	},

	updateConversationTimestamp(state: any, conversation_uuid: string) {
		let conversations = JSON.parse(
			JSON.stringify(state.state.conversations),
		);

		conversations.data = conversations.data.map((conversation) => {
			if (conversation.uuid === conversation_uuid) {
				conversation.updated_at = new Date();
			}
			return conversation;
		});

		state.commit('setConversations', conversations);
	},

	setArchived(state: any, payload: boolean) {
		state.commit('setArchived', payload);
	},
};
